import axios from 'axios'
import { SHOW_LOADER, HIDE_LOADER } from '../store/loader/actions';
import { store } from '../store/index'
import { toast } from 'react-toastify'
import { clearTemporaryStorage } from 'helpers/helpers'
import { getApiConfig, getLoginUrl } from 'helpers/env'
import { getClientHeaders, getDeviceId } from 'helpers/clientHeaders'
import i18n from '../i18n'

// Локальный бэкенд или боевой — выбирается переменной REACT_APP_API_TARGET,
// а не правкой этого файла руками (yarn start / yarn start:prod).
const { baseURL, uploadsBaseUrl } = getApiConfig()

export function resolveUploadUrl(path, folder = 'websites') {
	if (!path) return ''
	if (path.startsWith('http') || path.startsWith('data:')) return path
	return `${uploadsBaseUrl}/uploads/${folder}/${path}`
}

const defaultHeaders = {
	'Content-Type': 'application/json',
	'Accept': 'application/json',
}

/** Токен из Redux (актуален сразу после login); fallback — отдельный ключ localStorage */
function getAuthToken() {
	return store.getState()?.account?.token || localStorage.getItem('token') || null
}

function getLangHeaders() {
	const lang = localStorage.getItem('lang')
	if (!lang) return {}
	return {
		'Accept-Language': lang,
		'Language': lang,
	}
}

function attachRequestInterceptors(client, { withAuth = true } = {}) {
	client.interceptors.request.use((config) => {
		Object.assign(config.headers, getLangHeaders(), getClientHeaders())

		if (withAuth) {
			const token = getAuthToken()
			if (token) {
				config.headers.Authorization = `Bearer ${token}`
			}
		}

		return config
	})
}

/**
 * Потолок ожидания ответа, мс.
 *
 * Своего таймаута у клиента раньше не было вовсе, и запрос висел до упора
 * в nginx (fastcgi_read_timeout — 10 минут). Всё это время у человека просто
 * крутится страница; он её перезагружает — запрос обрывается, axios отдаёт
 * ECONNABORTED, и мы показывали «Превышено время ожидания», хотя сервер ещё
 * считал. Отсюда и жалобы на «частый таймаут» там, где запрос лёгкий.
 *
 * 3 минуты — выше самой тяжёлой боевой страницы (остатки, до 153 с) и ниже
 * request_terminate_timeout php-fpm (300 с): клиент сдаётся после сервера,
 * а не вместо него. Конкретному запросу можно передать свой { timeout }.
 */
const REQUEST_TIMEOUT = 180000

/** Общая точка для настроек запроса — чтобы { timeout } работал во всех методах, а не только в POST */
function requestConfig(settings = {}) {
	const config = {}
	if (settings.timeout) config.timeout = settings.timeout
	return config
}

const axiosClient = axios.create({
	baseURL: baseURL,
	withCredentials: true,
	timeout: REQUEST_TIMEOUT,
	headers: { ...defaultHeaders },
})

const axiosGuest = axios.create({
	baseURL: baseURL,
	withCredentials: true,
	headers: { ...defaultHeaders },
})

const axiosFile = axios.create({
	baseURL: baseURL,
	withCredentials: true,
	headers: {
		'Content-Type': 'multipart/form-data',
		'Accept': 'application/json',
		'withCredentials': true,
	},
})

const axiosFileDownload = axios.create({
	baseURL: baseURL,
	withCredentials: true,
	headers: {
		'withCredentials': true,
	},
})

attachRequestInterceptors(axiosClient)
attachRequestInterceptors(axiosGuest, { withAuth: false })
attachRequestInterceptors(axiosFile)
attachRequestInterceptors(axiosFileDownload)

export async function GET(URL, payload = {}, settings = {}) {
	var params = "";
	if (Object.entries(payload).length > 0) {
		params = getPath(payload);
	}

	if (settings.loader) store.dispatch(SHOW_LOADER());

	const data = await axiosClient.get(URL + params, requestConfig(settings)).catch(error => {
		if (settings.loader) store.dispatch(HIDE_LOADER());
		httpStatusChecker(error, settings)
		return error.response
	})

	if (settings.loader) store.dispatch(HIDE_LOADER());

	return data
}

export async function POST(URL, payload, settings = { notification: true }) {
	let jsonData = JSON.stringify(payload);

	if (settings.loader) store.dispatch(SHOW_LOADER());

	const data = await axiosClient.post(URL, jsonData, requestConfig(settings))
		.catch(error => {
			if (settings.loader) store.dispatch(HIDE_LOADER());
			httpStatusChecker(error, settings)
			return error.response
		})

	if (settings.loader) store.dispatch(HIDE_LOADER());

	if ((data && data.status === 200) && settings?.notification) {
		toast.dismiss();
		toast.success(i18n.t('success'))
	}

	return data
}

export async function PUT(URL, payload, settings = {}) {
	let jsonData = JSON.stringify(payload);

	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosClient.put(URL, jsonData, requestConfig(settings))
		.catch(error => {
			if (settings.loader) store.dispatch(HIDE_LOADER());
			httpStatusChecker(error, settings)
			return error.response
		});
	if (settings.loader) store.dispatch(HIDE_LOADER());
	if (data && data.status === 200 && settings?.notification !== false) {
		toast.dismiss();
		toast.success(i18n.t('success'))
	}
	return data
}

export async function PATCH(URL, payload = {}, settings = {}) {
	let jsonData = JSON.stringify(payload);

	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosClient.patch(URL, jsonData, requestConfig(settings))
		.catch(error => {
			if (settings.loader) store.dispatch(HIDE_LOADER());
			httpStatusChecker(error, settings)
			return error.response
		});
	if (settings.loader) store.dispatch(HIDE_LOADER());
	if (data && data.status === 200) {
		toast.dismiss();
		toast.success(i18n.t('success'))
	}
	return data
}

export async function FILE(URL, payload, settings = {}) {
	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosFile.post(URL, payload)
		.catch(error => {
			store.dispatch(HIDE_LOADER());
			httpStatusChecker(error, settings)
			return error.response
		});
	store.dispatch(HIDE_LOADER());
	return data
}

export async function FILE_D(URL, settings = {}) {
	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosFileDownload.get(URL, { responseType: 'blob' })
		.catch(error => {
			store.dispatch(HIDE_LOADER());
			toast.error('Ошибка' + error)
			return error.response
		});
	store.dispatch(HIDE_LOADER());
	return data
}

export async function DELETE(URL, payload, settings = {}) {
	var params = "";
	if (Object.entries(payload).length > 0) {
		params = getPath(payload, URL);
	}

	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosClient.delete(`${URL + params}`)
		.catch(error => {
			httpStatusChecker(error, settings)
			return error.response
		});
	store.dispatch(HIDE_LOADER());
	if (data && data.status === 200) {
		toast.dismiss();
		toast.success(i18n.t('deleted'))
	}
	return data

}

export async function GUESTGET(URL, payload, settings = {}) {
	if (settings.loader) store.dispatch(SHOW_LOADER());
	const data = await axiosGuest.get(`${URL}`)
		.catch(error => {
			httpStatusCheckerGuess(error, settings)
			return error.response
		});;
	store.dispatch(HIDE_LOADER());
	return data
}

export async function GUESTPOST(URL, payload, settings = {}) {
	let jsonData = JSON.stringify(payload);
	if (settings.loader) store.dispatch(SHOW_LOADER());
	var data
	data = await axiosGuest.post(`${URL}`, jsonData)
		.catch(error => {
			httpStatusCheckerGuess(error, settings)
			return error.response
		});
	store.dispatch(HIDE_LOADER());
	return data
}

export async function GUESTPUT(URL, payload, settings = {}) {
	let jsonData = JSON.stringify(payload);
	if (settings.loader) store.dispatch(SHOW_LOADER());
	var data
	data = await axiosGuest.put(`${URL}`, jsonData)
		.catch(error => {
			httpStatusCheckerGuess(error, settings)
			return error.response
		});
	store.dispatch(HIDE_LOADER());
	return data
}

function getPath(payload, url) {
	let iterations = Object.entries(payload).length;
	var pathArr = "?";
	if (url)
		url.includes("?") ? pathArr = '&' : pathArr = '?'

	for (let key in payload) {
		if (!--iterations) {
			pathArr += key + "=" + payload[key];
		} else {
			pathArr += key + "=" + payload[key] + "&";
		}
	}
	return pathArr;
}

function logoutOnUnauthorized() {
	clearTemporaryStorage()
	store.dispatch({ type: 'USER_LOGGED_OUT', payload: null })
	window.location.href = getLoginUrl();
}

function httpStatusChecker(error, settings = {}) {
	// { silent: true } — вызывающий код сам обрабатывает ошибку, тост показывать не надо
	if (settings?.silent) return

	toast.dismiss();

	const status = error?.response?.status
	const serverMessage = error?.response?.data?.message
	const fallback = i18n.t('error') || 'Ошибка'

	if (status === 400 || status === 403) {
		toast.error(serverMessage || fallback)
		return;
	}
	if (status === 401) {
		logoutOnUnauthorized()
		toast.error(i18n.t('login_or_password_invalid'))
		return;
	}
	if (status === 404) {
		toast.error(serverMessage || i18n.t('not_found'))
		return;
	}
	if (status === 415) {
		toast.error(fallback)
		return;
	}
	if (status === 500) {
		var message = ""
		if (error.response?.data?.file) {
			message += error.response.data.file
		}
		if (error.response?.data?.line) {
			message += error.response.data.line
		}
		if (error.response?.data?.message) {
			message += error.response.data.message
		}
		toast.error(message || fallback)
		return;
	}
	if (status === 408 || status === 502 || status === 503 || status === 504) {
		toast.error(serverMessage || 'Сервер не ответил вовремя. Попробуйте ещё раз.')
		return;
	}
	// ECONNABORTED axios отдаёт на ДВА разных события: свой таймаут и обрыв запроса
	// (перезагрузка страницы, уход в другой раздел, потеря сети). Раньше обе ветки
	// показывали «Превышено время ожидания», и обрыв по инициативе самого человека
	// выглядел как отказ сервера — по этому тосту искали проблему не там.
	if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
		const message = String(error?.message || '').toLowerCase()

		// Ушли со страницы / перезагрузили — молча, это не ошибка
		if (message.includes('aborted') || message.includes('canceled')) return;

		toast.error('Превышено время ожидания ответа сервера')
		return;
	}
	if (!error?.response) {
		toast.error('Нет связи с сервером')
		return;
	}
	toast.error(serverMessage || fallback)
}

function httpStatusCheckerGuess(error, settings = {}) {
	// { notification: false } — страница показывает ошибку сама, тост не нужен
	if (settings?.notification === false) return

	if (error?.response?.status === 400) {
		toast.error(error.response.data.message)
	}
	if (error?.response?.status === 401) {
		toast.error(i18n.t('login_or_password_invalid'))
	}
}

export function httpOk(response) {
	if (response?.status < 400) {
		return true
	} else {
		return false
	}
}

// Выход из кабинета: закрыть сеанс входа этого браузера в журнале «Входы».
// Зовётся ДО очистки токена — без него облако не узнает, чей сеанс. Ждём не дольше
// пары секунд: недоступный сервер не должен держать человека в кабинете.
export async function closeWebSession() {
	if (!getAuthToken()) return
	const request = POST('/logout', { device_id: getDeviceId(), app_code: 'web' }, { notification: false, silent: true, timeout: 2000 })
		.catch(() => null)
	await Promise.race([request, new Promise(resolve => setTimeout(resolve, 2000))])
}
